import html2canvas from 'html2canvas';

export const GANTT_SCHEDULE_EXPORT_CONTAINER_ID = 'schedule-container';

export const GANTT_EXPORT_VIEW_TYPES = {
  GANTT: 'gantt',
  LIST: 'list',
};

export const PDF_PAGE_MARGIN = 20;
export const LANDSCAPE_PAGE_HEIGHT = 595;
export const LANDSCAPE_PDF_CONTENT_WIDTH = 802;
export const LANDSCAPE_CONTENT_HEIGHT = LANDSCAPE_PAGE_HEIGHT - (PDF_PAGE_MARGIN * 2);
export const COMPANY_IMAGE_HEADER_HEIGHT = 130;

const isBackgroundPixel = (red, green, blue) => (
  red >= 240 && green >= 240 && blue >= 240
);

export const isCanvasBlank = (canvas) => {
  const context = canvas.getContext('2d');
  const { data, width, height } = context.getImageData(0, 0, canvas.width, canvas.height);
  const pixelCount = width * height;
  const sampleStride = Math.max(1, Math.floor(pixelCount / 10000));

  for (let pixelIndex = 0; pixelIndex < pixelCount; pixelIndex += sampleStride) {
    const dataIndex = pixelIndex * 4;
    const alpha = data[dataIndex + 3];

    if (alpha < 10) {
      continue;
    }

    const red = data[dataIndex];
    const green = data[dataIndex + 1];
    const blue = data[dataIndex + 2];

    if (!isBackgroundPixel(red, green, blue)) {
      return false;
    }
  }

  return true;
};

export const waitForNextPaint = () => new Promise((resolve) => {
  requestAnimationFrame(() => {
    requestAnimationFrame(resolve);
  });
});

export const captureGanttScheduleScreenshot = async (
  elementId = GANTT_SCHEDULE_EXPORT_CONTAINER_ID,
) => {
  const element = document.getElementById(elementId);
  if (!element) {
    throw new Error('Gantt schedule is not visible. Switch to the schedule view and try again.');
  }

  const canvas = await html2canvas(element, {
    useCORS: true,
    allowTaint: true,
    backgroundColor: '#ffffff',
    width: element.clientWidth,
    // Subtracting 100px to account for the header
    height: element.clientHeight - 100,
    scale: window.devicePixelRatio || 1,
  });

  return canvas.toDataURL('image/png');
};

const loadImage = (dataUrl) => new Promise((resolve, reject) => {
  const image = new Image();
  image.onload = () => resolve(image);
  image.onerror = reject;
  image.src = dataUrl;
});

export const getImageSizeFromDataUrl = async (dataUrl) => {
  const image = await loadImage(dataUrl);
  return {
    width: image.width,
    height: image.height,
  };
};

const sliceImageByHeights = async (dataUrl, sliceHeightsPx, slicePdfHeights) => {
  const image = await loadImage(dataUrl);
  const slices = [];
  let y = 0;

  sliceHeightsPx.forEach((sliceHeightPx, index) => {
    const height = Math.min(Math.round(sliceHeightPx), image.height - y);
    if (height <= 0) {
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = image.width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    context.drawImage(image, 0, y, image.width, height, 0, 0, image.width, height);

    if (!isCanvasBlank(canvas)) {
      slices.push({
        image: canvas.toDataURL('image/png'),
        height: slicePdfHeights[index],
      });
    }

    y += height;
  });

  return slices;
};

// Builds the PDF image pages for the gantt schedule
// Dealing with overflow by slicing the image into multiple pages
export const buildGanttPdfImagePages = async ({
  imageDataUrl,
  contentWidth,
  firstPageAvailableHeight,
  subsequentPageAvailableHeight,
}) => {
  const { width: imageWidth, height: imageHeight } = await getImageSizeFromDataUrl(imageDataUrl);
  const totalPdfHeight = contentWidth * (imageHeight / imageWidth);

  // If the total PDF height is less than the first page available height, return the image as is
  if (totalPdfHeight <= firstPageAvailableHeight) {
    return [{
      image: imageDataUrl,
      width: contentWidth,
    }];
  }

  const slicePdfHeights = [firstPageAvailableHeight];
  let remainingPdfHeight = totalPdfHeight - firstPageAvailableHeight;

  // Find the heights of the subsequent pages
  while (remainingPdfHeight > 0) {
    const sliceHeight = Math.min(subsequentPageAvailableHeight, remainingPdfHeight);
    slicePdfHeights.push(sliceHeight);
    remainingPdfHeight -= sliceHeight;
  }

  const sliceHeightsPx = slicePdfHeights.map(
    (pdfHeight) => (pdfHeight / totalPdfHeight) * imageHeight,
  );
  const slices = await sliceImageByHeights(imageDataUrl, sliceHeightsPx, slicePdfHeights);

  if (!slices.length) {
    return [{
      image: imageDataUrl,
      width: contentWidth,
    }];
  }

  // Map the slices to the PDF image pages
  return slices.map((slice, index) => {
    const content = {
      image: slice.image,
      width: contentWidth,
      height: slice.height,
    };

    if (index > 0) {
      content.pageBreak = 'before';
    }

    return content;
  });
};
